// routes/events/$identifier.tsx
// -----------------------------------------------------------------------------
// Event detail page — standalone screen for direct navigation (shared links,
// notifications). Renders the event content inline, not in a side panel.
// Public page: no auth required to view. RSVP gated behind login modal.
// Supports both UUID (occurrence ID) and invite code identifiers.
// -----------------------------------------------------------------------------

import { createFileRoute } from "@tanstack/react-router";
import { AppLayout, PageTransition, useHeaderConfig } from "@/components/layout";
import { EventDetailContent } from "@/components/events";
import { useFetchEventByIdQuery, useFetchEventByInviteCodeQuery, EventType } from "@/api/clubsApi";
import { ShareButton } from "@/components/ui/ShareButton";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute("/events/$identifier")({
  component: EventDetailPage,
});

function EventDetailPage() {
  const { identifier } = Route.useParams();
  const isUuid = UUID_REGEX.test(identifier);

  // If UUID, fetch by occurrence ID directly
  const { data: eventById } = useFetchEventByIdQuery(identifier, { skip: !isUuid });

  // If not UUID, fetch by invite code
  const { data: eventByCode } = useFetchEventByInviteCodeQuery(identifier, { skip: isUuid });

  const event = isUuid ? eventById : eventByCode;
  const eventId = event?.id ?? (isUuid ? identifier : undefined);
  const isHang = event?.rootEvent?.event_type === EventType.hang;
  const canShare =
    event?.rootEvent?.allows_public_link === true && !!event?.invite_code;
  const shareUrl = canShare
    ? `https://tiny.club/e/${event.invite_code}`
    : undefined;

  useHeaderConfig(
    {
      breadcrumbs: [
        { label: isHang ? "Hang" : "Event details" },
      ],
      actions:
        canShare && shareUrl ? (
          <ShareButton
            url={shareUrl}
            title={event?.rootEvent?.title || "Check out this event on TinyClub"}
          />
        ) : undefined,
    },
    [isHang, canShare, shareUrl, event?.rootEvent?.title]
  );

  return (
    <AppLayout>
      <PageTransition>
        <div className="px-4 py-6">
          <div className="max-w-[568px] mx-auto">
            {eventId && <EventDetailContent eventId={eventId} />}
          </div>
        </div>
      </PageTransition>
    </AppLayout>
  );
}
